import React, {
  useState,
  ChangeEvent,
  RefObject,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faSearch } from "@fortawesome/free-solid-svg-icons";
export interface SearchInputHandle {
  clear: () => void;
  getValue: () => void;
}
interface SearchInputProps {
  callback: (value: string) => void;
  searchManualCallback?: (value: string) => void;
  debounce: number;
  placeholder?: string;
  searchRef?: RefObject<HTMLInputElement | null>; // Keep for backward compatibility
  clearIconRight?: string;
  onClear?: () => void;
}

const SearchInput = forwardRef<SearchInputHandle, SearchInputProps>(
  (
    {
      callback,
      searchManualCallback,
      debounce,
      placeholder = "Search",
      searchRef,
      clearIconRight = "8%",
      onClear,
    },
    ref,
  ) => {
    const [timer, setTimer] = useState<number | null>(null);
    const [searchInput, setSearchInput] = useState<string>("");
    const [clearIconVisible, setClearIconVisible] = useState<boolean>(false);

    // Debounced search
    const debounceSearch = (event: ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;
      setSearchInput(val);
      setClearIconVisible(val.length > 0);
      if (timer) clearTimeout(timer);
      setTimer(setTimeout(() => callback(val), debounce));
    };

    // Paste handler
    const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData("text")
        .replace(/\r\n|\n/g, ",");
      const newValue = searchInput ? searchInput + pastedData : pastedData;
      setSearchInput(newValue);
      setClearIconVisible(true);
      if (timer) clearTimeout(timer);
      setTimer(setTimeout(() => callback(newValue), debounce));
    };

    // Clear search
    const clearSearch = () => {
      setSearchInput("");
      callback("");
      searchManualCallback?.("");
      onClear?.();
      setClearIconVisible(false);

      // Keep old searchRef behavior for backward compatibility
      if (searchRef && searchRef.current) {
        searchRef.current.value = "";
      }
    };

    // Expose clearSearch via ref
    useImperativeHandle(ref, () => ({
      clear: clearSearch,
      getValue: () => searchInput,
    }));

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && searchManualCallback) {
        searchManualCallback(searchInput);
      }
    };

    return (
      <div
        className="form-group"
        style={{ position: "relative", marginBottom: 0 }}
      >
        <input
          ref={searchRef} // Keep backward compatibility
          type="text"
          className="search-input"
          placeholder={placeholder}
          value={searchInput}
          onChange={debounceSearch}
          onKeyDown={handleKeyDown}
          onPaste={onPaste}
          style={{
            fontWeight: "normal",
            width: "100%",
            padding: clearIconVisible
              ? "10px 60px 10px 10px"
              : "10px 35px 10px 10px",
            boxSizing: "border-box",
            margin: "0px",
          }}
        />
        {clearIconVisible && (
          <span
            className="search-clear"
            onClick={clearSearch}
            style={{
              position: "absolute",
              right: "35px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              height: "20px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </span>
        )}
        <span
          className="search-icon"
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            height: "20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon
            icon={faSearch}
            onClick={() => searchManualCallback?.(searchInput)}
          />
        </span>
      </div>
    );
  },
);

export default SearchInput;
